import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col } from "stories/layout";
import { Input, DatePicker } from "stories/forms";
import "./derivativeAlignedRow.css";
import { Button, Typography } from "stories/components";
import DerivativeTerminationContractCard from "views/app/OptionEdit/DerivativeTerminationContractCard";
import TerminationSettlementFields from "views/app/OptionEdit/TerminationSettlementFields";
import {
  buildTerminationContracts,
  getContractLotSize,
  getContractLotAmount,
  updateTerminatedContractField,
} from "views/app/OptionEdit/derivativeTerminationUtils";

const parseLotSizeInput = (value) => {
  const parsed = parseInt(String(value ?? "").replace(",", ".").replace(/\s/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const DerivativeTerminationPanel = ({
  option,
  disabled = false,
  stockMarket,
  onConfirm,
  onResultChange,
  isOpen: controlledIsOpen,
  onIsOpenChange,
  terminationConfirmed = false,
}) => {
  const { t } = useTranslation();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen ?? internalIsOpen;
  const setIsOpen = onIsOpenChange ?? setInternalIsOpen;
  const [terminationLotSizeInput, setTerminationLotSizeInput] = useState("");
  const [eventDate, setEventDate] = useState(new Date());
  const [remainingTradeId, setRemainingTradeId] = useState("");
  const [confirmedResult, setConfirmedResult] = useState(null);
  const [unitPrice, setUnitPrice] = useState("");
  const [totalPrice, setTotalPrice] = useState("");

  useEffect(() => {
    if (!terminationConfirmed) {
      setConfirmedResult(null);
      setTerminationLotSizeInput("");
      setEventDate(new Date());
      setRemainingTradeId("");
      setUnitPrice("");
      setTotalPrice("");
    }
  }, [terminationConfirmed]);

  const originalLotSize = useMemo(() => getContractLotSize(option), [option?.lot_size]);
  const lotAmount = useMemo(() => getContractLotAmount(option), [option?.lot_amount]);

  const parsedTerminationLotSize = useMemo(() => {
    const parsed = parseLotSizeInput(terminationLotSizeInput);
    if (parsed == null) {
      return null;
    }
    return Math.min(Math.max(parsed, 0), originalLotSize);
  }, [terminationLotSizeInput, originalLotSize]);

  const remainingLotSize = useMemo(() => {
    if (parsedTerminationLotSize == null) {
      return originalLotSize;
    }
    return Math.max(originalLotSize - parsedTerminationLotSize, 0);
  }, [originalLotSize, parsedTerminationLotSize]);

  const displayRemainingLotSize = useMemo(() => {
    if (terminationLotSizeInput === "" || terminationLotSizeInput == null) {
      return originalLotSize;
    }
    if (parsedTerminationLotSize == null) {
      return "";
    }
    return remainingLotSize;
  }, [terminationLotSizeInput, parsedTerminationLotSize, remainingLotSize, originalLotSize]);

  const terminatedQuantity = useMemo(() => {
    if (parsedTerminationLotSize == null) {
      return 0;
    }
    return parsedTerminationLotSize * lotAmount;
  }, [parsedTerminationLotSize, lotAmount]);

  const handleQuantityChange = (value) => {
    const parsed = parseInt(String(value ?? "").replace(",", ".").replace(/\s/g, ""), 10);
    if (!Number.isFinite(parsed) || lotAmount <= 0) {
      return;
    }
    const nextLotSize = Math.min(Math.max(Math.round(parsed / lotAmount), 0), originalLotSize);
    setTerminationLotSizeInput(String(nextLotSize));
  };

  const lotSizeError = useMemo(() => {
    if (!isOpen) {
      return "";
    }
    if (terminationLotSizeInput === "" || terminationLotSizeInput == null) {
      return t("derivative_termination_lot_size_required", "Anna terminoitava erän koko");
    }
    const parsed = parseLotSizeInput(terminationLotSizeInput);
    if (parsed == null || parsed <= 0) {
      return t("derivative_termination_lot_size_positive", "Erän koon tulee olla kokonaisluku ja suurempi kuin 0");
    }
    if (parsed > originalLotSize) {
      return t(
        "derivative_termination_lot_size_max",
        "Erän koko ei voi ylittää alkuperäistä ({{max}})",
        { max: originalLotSize }
      );
    }
    return "";
  }, [isOpen, terminationLotSizeInput, originalLotSize, t]);

  const handleConfirm = () => {
    if (lotSizeError || parsedTerminationLotSize == null) {
      return;
    }

    const result = buildTerminationContracts(option, {
      terminationLotSize: parsedTerminationLotSize,
      eventDate,
      remainingTradeId: remainingTradeId.trim(),
    });
    result.terminated = {
      ...result.terminated,
      termination_price: unitPrice,
      receives: totalPrice,
      termination_fee: unitPrice || result.terminated.termination_fee,
    };

    setConfirmedResult(result);
    setIsOpen(false);
    onConfirm?.(result);
  };

  const handleTerminatedFieldChange = (field, value) => {
    setConfirmedResult((prev) => {
      if (!prev?.terminated) {
        return prev;
      }
      const next = {
        ...prev,
        terminated: updateTerminatedContractField(prev.terminated, field, value),
      };
      onResultChange?.(next);
      return next;
    });
  };

  const handleNewContractFieldChange = (field, value) => {
    setConfirmedResult((prev) => {
      if (!prev?.remaining) {
        return prev;
      }
      const next = {
        ...prev,
        remaining: updateTerminatedContractField(prev.remaining, field, value),
      };
      onResultChange?.(next);
      return next;
    });
  };

  const optionState = Number(option?.option_state);
  const canTerminate = optionState === 2 && !confirmedResult;

  if (!option?.id || (!isOpen && !confirmedResult)) {
    return null;
  }

  return (
    <>
      {isOpen && canTerminate && (
        <div className="derivative-termination-panel border rounded px-3 py-3 mb-3" style={{ backgroundColor: "#f8f9fe" }}>
          <Typography variant="small" className="text-muted mb-3">
            {t(
              "derivative_termination_help",
              "Anna terminoitava erän koko. Erien määrä ei muutu. Jos erän koko on sama kuin alkuperäinen, koko sopimus terminoidaan. Pienempi arvo luo kaksi sopimusta: terminoitu osa ja jäljellä oleva avoin osa. Kokonaismäärä = erän koko × erien määrä."
            )}
          </Typography>

          <Row className="derivative-aligned-row">
            <Col xs="12" md="6" className="mb-3">
              <Input
                label={t("derivative_termination_lot_size", "Terminoitava erän koko")}
                name="derivative_termination_lot_size"
                type="number"
                min={1}
                step={1}
                value={terminationLotSizeInput}
                onChange={({ target }) => setTerminationLotSizeInput(target.value)}
              />
            </Col>
            <Col xs="12" md="6" className="mb-3">
              <Input
                name="derivative_termination_remaining_lot_size"
                type="text"
                label={t("derivative_termination_remaining_lot_size", "Jäljellä avoimena (erän koko)")}
                value={displayRemainingLotSize}
                disabled
                readOnly
              />
            </Col>
          </Row>

          <TerminationSettlementFields
            quantity={terminatedQuantity}
            quantityReadOnly={false}
            onQuantityChange={handleQuantityChange}
            unitPrice={unitPrice}
            onUnitPriceChange={setUnitPrice}
            total={totalPrice}
            onTotalChange={setTotalPrice}
            strikePrice={option?.strike_price}
            role={option?.role}
            optionRight={option?.option_right}
            derivativeType={option?.derivative_type}
          />

          <Row className="derivative-aligned-row">
            <Col xs="12" md="6" className="mb-3">
              <DatePicker
                label={t("derivative_termination_event_date", "Tapahtumapäivä")}
                name="derivative_termination_event_date"
                className="form-control"
                selected={eventDate}
                value={eventDate}
                onChange={(date) => setEventDate(date)}
                showYearDropdown
                dateFormat="d.M.yyyy"
              />
            </Col>
            {remainingLotSize > 0 && (
              <Col xs="12" md="6" className="mb-3">
                <Input
                  label={t("derivative_termination_new_trade_id", "Tunnus")}
                  name="derivative_termination_new_trade_id"
                  type="text"
                  value={remainingTradeId}
                  onChange={({ target }) => setRemainingTradeId(target.value)}
                />
              </Col>
            )}
          </Row>

          {lotSizeError && (
            <Typography variant="small" className="text-danger mb-3">
              {lotSizeError}
            </Typography>
          )}

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <Button
              className="sk-quick-actions__btn"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={() => setIsOpen(false)}
            >
              {t("derivative_termination_cancel", "Peruuta")}
            </Button>
            <Button
              className="sk-quick-actions__btn"
              variant="secondary"
              size="sm"
              disabled={disabled || Boolean(lotSizeError)}
              onClick={handleConfirm}
            >
              <i className="fas fa-check sk-quick-actions__icon" aria-hidden />
              <span>{t("derivative_termination_confirm", "Vahvista terminointi")}</span>
            </Button>
          </div>
        </div>
      )}

      {confirmedResult && (
        <div className="derivative-termination-results mb-3">
          <DerivativeTerminationContractCard
            title={t("derivative_termination_terminated_from_original", "Alkuperäisestä terminoitu")}
            contract={confirmedResult.terminated}
            stockMarket={stockMarket}
            settlementEditable
            onFieldChange={handleTerminatedFieldChange}
          />

          {confirmedResult.remaining && (
            <DerivativeTerminationContractCard
              title={t("derivative_termination_new_contract", "Uusi sopimus")}
              contract={confirmedResult.remaining}
              stockMarket={stockMarket}
              editable
              onFieldChange={handleNewContractFieldChange}
            />
          )}
        </div>
      )}
    </>
  );
};

export default DerivativeTerminationPanel;
